/**
 * /admin/settings — operator-editable runtime configuration (OCP-72).
 *
 * Renders the SETTINGS_REGISTRY catalog grouped by category. Each
 * setting has type-specific input (string / number / boolean toggle /
 * string-array editor / multi-line text). The compose-template setting
 * gets a dedicated full-screen editor with diff-view + "Test parse"
 * preview.
 */
import { useCallback, useEffect, useMemo, useState } from 'react';

import { SesQuotaBanner } from '../components/SesQuotaBanner';
import { PushNotificationsCard } from '../components/PushNotificationsCard';
import { AppearanceSettings } from '../components/AppearanceSettings';
import { api, ApiError } from '../lib/api';
import { fmtDateTime } from '../lib/format-time';

type ValueType = 'string' | 'number' | 'boolean' | 'string[]' | 'object' | 'text-multiline';
type Category = 'tenant' | 'mail' | 'ocp' | 'worker' | 'redeem' | 'telemetry' | 'maintenance' | 'ai' | 'feedback' | 'support';
type Source = 'db' | 'env' | 'default';
type DriftReason = 'baseline_unknown' | 'default_changed';

// OCP-417 drift block - non-null only for DB-sourced overrides.
interface SettingDrift {
  drifted: boolean;
  reason: DriftReason | null;
  baseline: unknown;
  currentDefault: unknown;
}

interface SettingView {
  key: string;
  category: Category;
  valueType: ValueType;
  description: string;
  default: unknown;
  value: unknown;
  source: Source;
  envVar: string | null;
  updatedAt: string | null;
  updatedByUserId: string | null;
  editable: boolean;
  sensitive: boolean;
  drift: SettingDrift | null;
}

const CATEGORY_ORDER: Category[] = ['tenant', 'mail', 'ocp', 'worker', 'redeem', 'telemetry', 'maintenance', 'ai', 'feedback', 'support'];
const CATEGORY_LABELS: Record<Category, string> = {
  tenant: 'Tenant defaults',
  mail: 'Mail',
  ocp: 'orboto Control self',
  worker: 'Worker pool',
  redeem: 'Redeem flow',
  telemetry: 'Telemetry',
  maintenance: 'Maintenance',
  ai: 'Managed AI',
  feedback: 'Feedback intake',
  support: 'Support inbox',
};

// OCP-335 — App tabs are client/device preferences, not runtime config.
type AppTab = 'app.notifications' | 'app.appearance';
type Tab = AppTab | Category;

const APP_TABS: { key: AppTab; label: string }[] = [
  { key: 'app.notifications', label: 'Notifications' },
  { key: 'app.appearance', label: 'Appearance' },
];

function initialTab(): Tab {
  try {
    const t = new URLSearchParams(window.location.search).get('tab');
    if (t && (CATEGORY_ORDER as string[]).includes(t)) return t as Category;
  } catch {
    // no location (tests without a window URL) - the default tab
  }
  return 'app.notifications';
}

export function AdminSettingsPage() {
  const [items, setItems] = useState<SettingView[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  // OCP-335 - land on the App / Notifications tab by default; OCP-617 - `?tab=<category>` opens that tab (the support inbox links here).
  const [activeTab, setActiveTab] = useState<Tab>(() => initialTab());

  const load = useCallback(async () => {
    setError(null);
    try {
      const r = await api<{ items: SettingView[] }>('/v1/admin/settings');
      setItems(r.items);
    } catch (err) {
      if (err instanceof ApiError) setError(`Could not load settings (HTTP ${err.status}).`);
      else setError('Could not load settings.');
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  const grouped = useMemo(() => {
    const out = new Map<Category, SettingView[]>();
    if (!items) return out;
    for (const s of items) {
      const arr = out.get(s.category) ?? [];
      arr.push(s);
      out.set(s.category, arr);
    }
    return out;
  }, [items]);

  const isAppTab = activeTab === 'app.notifications' || activeTab === 'app.appearance';
  const visibleItems = isAppTab ? [] : grouped.get(activeTab as Category) ?? [];
  const driftedItems = useMemo(() => (items ?? []).filter((s) => s.drift?.drifted), [items]);

  const tabClass = (active: boolean) =>
    `-mb-px shrink-0 border-b-2 px-3 py-2 text-sm ${
      active
        ? 'border-brand-500 text-brand-700'
        : 'border-transparent text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-100'
    }`;

  return (
    <div className="mx-auto max-w-5xl">
      <header className="mb-6">
        <h1 className="text-xl font-semibold text-gray-900 dark:text-gray-100">Settings</h1>
        <p className="mt-1 text-sm text-gray-600 dark:text-gray-400">
          {isAppTab
            ? 'Preferences for this device — notifications and appearance. These stay on your browser and never touch server config.'
            : 'Operator-editable runtime configuration. Changes take effect on the next request - no container restart. Secrets (API tokens, encryption keys) stay env-only and aren’t shown here; the few editable secrets (the support mailbox password) are write-only and stored encrypted.'}
        </p>
      </header>

      {error && (
        <div role="alert" className="mb-4 rounded border border-red-200 dark:border-red-800 bg-red-50 dark:bg-red-900/30 px-3 py-2 text-sm text-red-700 dark:text-red-300">
          {error}
        </div>
      )}

      {!isAppTab && driftedItems.length > 0 && (
        <div className="mb-4 rounded border border-amber-300 dark:border-amber-700 bg-amber-50 dark:bg-amber-900/30 px-3 py-2 text-sm text-amber-800 dark:text-amber-200">
          <span className="font-medium">
            {driftedItems.length} setting{driftedItems.length === 1 ? '' : 's'} drifted from the built-in default.
          </span>{' '}
          Open the matching tab and use “Resolve drift”:{' '}
          {driftedItems.map((s, i) => (
            <span key={s.key}>
              {i > 0 && ', '}
              <button
                type="button"
                onClick={() => setActiveTab(s.category)}
                className="font-mono underline decoration-dotted hover:text-amber-900 dark:hover:text-amber-100"
              >
                {s.key}
              </button>
            </span>
          ))}
        </div>
      )}

      {/* Two-group tab nav: App (device prefs) | Runtime config. */}
      <nav className="mb-6 flex flex-wrap items-center gap-1 overflow-x-auto border-b border-gray-200 dark:border-gray-800">
        <span className="px-2 text-[10px] font-semibold uppercase tracking-wider text-gray-400">App</span>
        {APP_TABS.map((t) => (
          <button key={t.key} type="button" onClick={() => setActiveTab(t.key)} className={tabClass(activeTab === t.key)}>
            {t.label}
          </button>
        ))}
        {items && (
          <>
            <span className="mx-1 h-5 w-px shrink-0 bg-gray-200 dark:bg-gray-700" aria-hidden="true" />
            <span className="px-2 text-[10px] font-semibold uppercase tracking-wider text-gray-400">Runtime config</span>
            {CATEGORY_ORDER.filter((c) => grouped.has(c)).map((c) => (
              <button key={c} type="button" onClick={() => setActiveTab(c)} className={tabClass(activeTab === c)}>
                {CATEGORY_LABELS[c]}
                <span className="ml-1 text-xs text-gray-400">({grouped.get(c)!.length})</span>
              </button>
            ))}
          </>
        )}
      </nav>

      {/* App tabs — client/device preferences. */}
      {activeTab === 'app.notifications' && (
        <div className="space-y-4">
          <PushNotificationsCard />
        </div>
      )}
      {activeTab === 'app.appearance' && (
        <div className="space-y-4">
          <AppearanceSettings />
        </div>
      )}

      {/* Runtime-config tabs. */}
      {!isAppTab && (
        <>
          {/* OCP-94: SES sandbox-mode warning, only relevant to runtime config. */}
          <SesQuotaBanner />
          {!items && !error && <p className="text-sm text-gray-500">Loading…</p>}
          {activeTab === 'mail' && <MailProviderIdentityPreview version={items} />}
          <div className="space-y-4">
            {visibleItems.map((s) => (
              <SettingRow key={s.key} setting={s} onChanged={load} />
            ))}
          </div>
        </>
      )}
    </div>
  );
}

// ---- SettingRow -----------------------------------------------------

function SettingRow({ setting, onChanged }: { setting: SettingView; onChanged: () => Promise<void> }) {
  // OCP-617 - an editable secret is write-only: the input starts empty, the stored value is never shown.
  const secret = setting.sensitive && setting.editable;
  const shown = (v: unknown) => (secret ? '' : formatForInput(v, setting.valueType));
  const [draft, setDraft] = useState<string>(() => shown(setting.value));
  const [saving, setSaving] = useState(false);
  const [resetting, setResetting] = useState(false);
  const [localError, setLocalError] = useState<string | null>(null);
  // OCP-445: non-blocking save warnings (e.g. a default region no worker serves).
  const [localWarnings, setLocalWarnings] = useState<string[]>([]);
  const [showCompose, setShowCompose] = useState(false);
  const [showDriftResolve, setShowDriftResolve] = useState(false);

  useEffect(() => {
    setDraft(secret ? '' : formatForInput(setting.value, setting.valueType));
  }, [setting.value, setting.valueType, secret]);

  const dirty = draft !== shown(setting.value);

  const handleSave = useCallback(async () => {
    setLocalError(null);
    setLocalWarnings([]);
    setSaving(true);
    try {
      const parsed = parseFromInput(draft, setting.valueType);
      const resp = await api<{ warnings?: string[] }>(`/v1/admin/settings/${encodeURIComponent(setting.key)}`, {
        method: 'PATCH',
        body: { value: parsed },
      });
      // OCP-445: the save succeeded; surface any non-blocking vocabulary warnings.
      if (resp?.warnings && resp.warnings.length > 0) setLocalWarnings(resp.warnings);
      await onChanged();
    } catch (err) {
      if (err instanceof ApiError && err.status === 400) {
        const body = err.body as { reason?: string } | null;
        setLocalError(body?.reason ?? 'invalid value');
      } else if (err instanceof Error) {
        setLocalError(err.message);
      } else {
        setLocalError('save failed');
      }
    } finally {
      setSaving(false);
    }
  }, [draft, setting.key, setting.valueType, onChanged]);

  const handleReset = useCallback(async () => {
    if (!window.confirm(`Reset ${setting.key} to default?`)) return;
    setResetting(true);
    setLocalError(null);
    try {
      await api(`/v1/admin/settings/${encodeURIComponent(setting.key)}`, { method: 'DELETE' });
      await onChanged();
    } catch (err) {
      if (err instanceof Error) setLocalError(err.message);
      else setLocalError('reset failed');
    } finally {
      setResetting(false);
    }
  }, [setting.key, onChanged]);

  const isCompose = setting.key === 'tenant.compose_template';

  return (
    <div className="rounded-lg border border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-900 p-4 shadow-sm">
      <div className="flex items-baseline justify-between gap-3">
        <div>
          <h3 className="font-mono text-sm font-medium text-gray-900 dark:text-gray-100">{setting.key}</h3>
          <p className="mt-1 text-sm text-gray-600 dark:text-gray-400">{setting.description}</p>
        </div>
        <div className="flex shrink-0 items-center gap-1.5">
          {setting.drift?.drifted && <DriftBadge reason={setting.drift.reason} />}
          <SourceBadge source={setting.source} />
        </div>
      </div>

      {setting.drift?.drifted && (
        <div
          role="alert"
          className="mt-3 flex flex-wrap items-center justify-between gap-2 rounded border border-amber-300 dark:border-amber-700 bg-amber-50 dark:bg-amber-900/30 px-3 py-2 text-xs text-amber-800 dark:text-amber-200"
        >
          <span>
            {setting.drift.reason === 'baseline_unknown'
              ? 'Saved before drift tracking - review this override once and re-baseline or reset it.'
              : 'The built-in default changed since this override was saved. The override still serves the old value.'}
          </span>
          <button
            type="button"
            onClick={() => setShowDriftResolve(true)}
            className="shrink-0 rounded bg-amber-600 px-2 py-1 font-medium text-white hover:bg-amber-700"
          >
            Resolve drift
          </button>
        </div>
      )}

      <div className="mt-3">
        {isCompose ? (
          <ComposeButtonRow
            preview={String(setting.value).split('\n').slice(0, 3).join('\n') + '\n…'}
            editable={setting.editable}
            onOpen={() => setShowCompose(true)}
          />
        ) : (
          <FieldEditor
            valueType={setting.valueType}
            value={draft}
            onChange={setDraft}
            editable={setting.editable}
            secret={secret}
            secretSet={secret && setting.value !== ''}
          />
        )}
      </div>

      {localError && (
        <div role="alert" className="mt-2 rounded border border-red-200 dark:border-red-800 bg-red-50 dark:bg-red-900/30 px-2 py-1 text-xs text-red-800 dark:text-red-300">
          {localError}
        </div>
      )}

      {localWarnings.length > 0 && (
        <div
          role="status"
          className="mt-2 rounded border border-amber-300 dark:border-amber-700 bg-amber-50 dark:bg-amber-900/30 px-2 py-1 text-xs text-amber-800 dark:text-amber-200"
        >
          {/* OCP-445: saved, but the value is worth a second look (e.g. a default region no worker serves). */}
          {localWarnings.map((w, i) => (
            <p key={i} className={i > 0 ? 'mt-1' : undefined}>
              Saved with a warning: {w}
            </p>
          ))}
        </div>
      )}

      <div className="mt-3 flex items-center justify-between">
        <div className="text-xs text-gray-500">
          {setting.envVar && (
            <span>
              env fallback: <code className="rounded bg-gray-100 dark:bg-gray-800 px-1">{setting.envVar}</code> ·{' '}
            </span>
          )}
          default: <code className="rounded bg-gray-100 dark:bg-gray-800 px-1">{formatForLabel(setting.default, setting.valueType)}</code>
          {setting.updatedAt && (
            <span className="ml-2">
              · last changed {fmtDateTime(setting.updatedAt)}
            </span>
          )}
        </div>
        <div className="flex gap-2">
          {setting.source === 'db' && (
            <button
              type="button"
              onClick={handleReset}
              disabled={resetting}
              className="rounded border border-gray-300 dark:border-gray-700 px-2 py-1 text-xs hover:bg-gray-50 dark:hover:bg-gray-800/60 disabled:opacity-50"
            >
              {resetting ? 'Resetting…' : 'Reset to default'}
            </button>
          )}
          {!isCompose && (
            <button
              type="button"
              onClick={handleSave}
              disabled={!dirty || saving || !setting.editable}
              className="rounded bg-brand-500 px-3 py-1 text-xs font-medium text-white hover:bg-brand-600 disabled:cursor-not-allowed disabled:opacity-50"
            >
              {saving ? 'Saving…' : 'Save'}
            </button>
          )}
        </div>
      </div>

      {showCompose && (
        <ComposeEditor
          currentValue={String(setting.value)}
          editable={setting.editable}
          onClose={() => setShowCompose(false)}
          onSaved={async () => {
            setShowCompose(false);
            await onChanged();
          }}
        />
      )}

      {showDriftResolve && setting.drift && (
        <DriftResolveDialog
          setting={setting}
          onClose={() => setShowDriftResolve(false)}
          onResolved={async () => {
            setShowDriftResolve(false);
            await onChanged();
          }}
        />
      )}
    </div>
  );
}

// ---- DriftBadge -----------------------------------------------------

function DriftBadge({ reason }: { reason: DriftReason | null }) {
  return (
    <span
      className="shrink-0 rounded bg-amber-100 dark:bg-amber-900/50 px-1.5 py-0.5 text-xs font-medium text-amber-800 dark:text-amber-200"
      title={
        reason === 'baseline_unknown'
          ? 'Override saved before drift tracking - baseline unknown.'
          : 'The registry default changed since this override was saved.'
      }
    >
      drifted
    </span>
  );
}

// ---- DriftResolveDialog ---------------------------------------------

/**
 * OCP-417 one-click drift resolution. Three choices:
 *  - Sync: drop the override (DELETE) → take the new default.
 *  - Keep: keep the override value but re-baseline it against the new
 *    default (POST /rebaseline) so it stops showing as drifted.
 *  - Merge: side-by-side line diff of the old default (baseline) vs the
 *    new default, plus the current override - for template-typed
 *    settings. No freeform re-typing required for sync/keep.
 */
function DriftResolveDialog({
  setting,
  onClose,
  onResolved,
}: {
  setting: SettingView;
  onClose: () => void;
  onResolved: () => Promise<void>;
}) {
  const [busy, setBusy] = useState<null | 'sync' | 'keep'>(null);
  const [error, setError] = useState<string | null>(null);

  const drift = setting.drift!;
  const isTemplate = setting.valueType === 'text-multiline' || setting.valueType === 'object';
  const baselineText = drift.baseline == null ? '(unknown - saved before drift tracking)' : valueToText(drift.baseline);
  const currentDefaultText = valueToText(drift.currentDefault);
  const overrideText = valueToText(setting.value);
  const diffLines = useMemo(
    () => (drift.baseline == null ? [] : computeLineDiff(baselineText, currentDefaultText)),
    [drift.baseline, baselineText, currentDefaultText],
  );

  const act = useCallback(
    async (action: 'sync' | 'keep') => {
      setError(null);
      setBusy(action);
      try {
        if (action === 'sync') {
          await api(`/v1/admin/settings/${encodeURIComponent(setting.key)}`, { method: 'DELETE' });
        } else {
          await api(`/v1/admin/settings/${encodeURIComponent(setting.key)}/rebaseline`, { method: 'POST' });
        }
        await onResolved();
      } catch (err) {
        if (err instanceof ApiError) {
          const body = err.body as { reason?: string } | null;
          setError(body?.reason ?? `resolve failed (HTTP ${err.status})`);
        } else if (err instanceof Error) {
          setError(err.message);
        } else {
          setError('resolve failed');
        }
        setBusy(null);
      }
    },
    [setting.key, onResolved],
  );

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
      <div className="flex max-h-full w-full max-w-3xl flex-col rounded-lg bg-white dark:bg-gray-900 shadow-xl">
        <header className="flex items-start justify-between border-b border-gray-200 dark:border-gray-800 px-4 py-3">
          <div>
            <h2 className="text-base font-semibold text-gray-900 dark:text-gray-100">Resolve drift</h2>
            <p className="mt-0.5 font-mono text-xs text-gray-600 dark:text-gray-400">{setting.key}</p>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="rounded border border-gray-300 dark:border-gray-700 px-3 py-1 text-sm hover:bg-gray-50 dark:hover:bg-gray-800/60"
          >
            Close
          </button>
        </header>

        <div className="flex-1 overflow-y-auto px-4 py-3 text-sm">
          <p className="text-gray-700 dark:text-gray-300">
            {drift.reason === 'baseline_unknown'
              ? 'This override has no recorded baseline (saved before drift tracking). Sync to take the current default, or Keep to accept the current value and start tracking it against today’s default.'
              : 'The built-in default changed since this override was saved. Choose how to resolve it:'}
          </p>

          {isTemplate ? (
            <div className="mt-3">
              <h3 className="text-xs font-semibold uppercase tracking-wide text-gray-500">
                Diff - old default (baseline) vs new default
              </h3>
              <pre className="mt-2 max-h-64 overflow-auto rounded border border-gray-200 dark:border-gray-800 bg-gray-50 dark:bg-gray-800 p-2 text-xs font-mono whitespace-pre-wrap">
                {drift.baseline == null
                  ? '(no baseline to diff - this override was saved before drift tracking)'
                  : diffLines.length === 0
                    ? '(defaults are identical - no diff)'
                    : diffLines.map((line, i) => (
                        <div
                          key={i}
                          className={
                            line.kind === 'add'
                              ? 'bg-green-100 text-green-900'
                              : line.kind === 'remove'
                                ? 'bg-red-100 text-red-900 dark:text-red-200'
                                : 'text-gray-700 dark:text-gray-300'
                          }
                        >
                          {line.prefix}
                          {line.text}
                        </div>
                      ))}
              </pre>
              <details className="mt-2">
                <summary className="cursor-pointer text-xs text-gray-500">Show current override value</summary>
                <pre className="mt-1 max-h-48 overflow-auto rounded border border-gray-200 dark:border-gray-800 bg-gray-50 dark:bg-gray-800 p-2 text-xs font-mono whitespace-pre-wrap">
                  {overrideText}
                </pre>
              </details>
            </div>
          ) : (
            <dl className="mt-3 grid grid-cols-1 gap-2 sm:grid-cols-3">
              <ValueCard label="Old default (baseline)" text={baselineText} />
              <ValueCard label="New default" text={currentDefaultText} />
              <ValueCard label="Your override" text={overrideText} />
            </dl>
          )}
        </div>

        {error && (
          <div role="alert" className="border-t border-red-200 dark:border-red-800 bg-red-50 dark:bg-red-900/30 px-4 py-2 text-sm text-red-800 dark:text-red-300">
            {error}
          </div>
        )}

        <footer className="flex flex-wrap items-center justify-end gap-2 border-t border-gray-200 dark:border-gray-800 px-4 py-3">
          <button
            type="button"
            onClick={() => act('sync')}
            disabled={busy !== null}
            className="rounded border border-gray-300 dark:border-gray-700 px-3 py-1.5 text-sm hover:bg-gray-50 dark:hover:bg-gray-800/60 disabled:opacity-50"
            title="Drop the override and take the new default."
          >
            {busy === 'sync' ? 'Syncing…' : 'Sync (take new default)'}
          </button>
          <button
            type="button"
            onClick={() => act('keep')}
            disabled={busy !== null}
            className="rounded bg-brand-500 px-3 py-1.5 text-sm font-medium text-white hover:bg-brand-600 disabled:cursor-not-allowed disabled:opacity-50"
            title="Keep the override value and re-baseline it against the new default."
          >
            {busy === 'keep' ? 'Keeping…' : 'Keep override (re-baseline)'}
          </button>
        </footer>
      </div>
    </div>
  );
}

function ValueCard({ label, text }: { label: string; text: string }) {
  return (
    <div className="rounded border border-gray-200 dark:border-gray-800 bg-gray-50 dark:bg-gray-800 p-2">
      <dt className="text-xs font-semibold uppercase tracking-wide text-gray-500">{label}</dt>
      <dd className="mt-1 break-words font-mono text-xs text-gray-800 dark:text-gray-200">{text}</dd>
    </div>
  );
}

function valueToText(value: unknown): string {
  if (value == null) return '-';
  if (typeof value === 'string') return value;
  if (typeof value === 'object') return JSON.stringify(value, null, 2);
  return String(value);
}

// ---- FieldEditor ---------------------------------------------------

function FieldEditor({
  valueType,
  value,
  onChange,
  editable,
  secret = false,
  secretSet = false,
}: {
  valueType: ValueType;
  value: string;
  onChange: (v: string) => void;
  editable: boolean;
  /** OCP-617 - a write-only secret: a password input, never prefilled. */
  secret?: boolean;
  /** Whether a value is stored (the list only says "set", never the value). */
  secretSet?: boolean;
}) {
  if (secret) {
    return (
      <input
        type="password"
        autoComplete="new-password"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        disabled={!editable}
        placeholder={secretSet ? 'set - type a new value to replace it' : 'not set'}
        className="w-full rounded border border-gray-300 dark:border-gray-700 px-2 py-1 text-sm"
      />
    );
  }
  if (valueType === 'boolean') {
    const checked = value === 'true';
    return (
      <label className="flex items-center gap-2 text-sm">
        <input
          type="checkbox"
          checked={checked}
          onChange={(e) => onChange(e.target.checked ? 'true' : 'false')}
          disabled={!editable}
        />
        <span>{checked ? 'Enabled' : 'Disabled'}</span>
      </label>
    );
  }
  if (valueType === 'number') {
    return (
      <input
        type="number"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        disabled={!editable}
        className="w-full rounded border border-gray-300 dark:border-gray-700 px-2 py-1 text-sm tabular-nums"
      />
    );
  }
  if (valueType === 'string[]') {
    return (
      <input
        type="text"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        disabled={!editable}
        placeholder="comma-separated values"
        className="w-full rounded border border-gray-300 dark:border-gray-700 px-2 py-1 font-mono text-sm"
      />
    );
  }
  if (valueType === 'text-multiline') {
    return (
      <textarea
        value={value}
        onChange={(e) => onChange(e.target.value)}
        disabled={!editable}
        rows={6}
        className="w-full rounded border border-gray-300 dark:border-gray-700 px-2 py-1 font-mono text-xs"
      />
    );
  }
  if (valueType === 'object') {
    return (
      <textarea
        value={value}
        onChange={(e) => onChange(e.target.value)}
        disabled={!editable}
        rows={4}
        placeholder="JSON object"
        className="w-full rounded border border-gray-300 dark:border-gray-700 px-2 py-1 font-mono text-xs"
      />
    );
  }
  // string
  return (
    <input
      type="text"
      value={value}
      onChange={(e) => onChange(e.target.value)}
      disabled={!editable}
      className="w-full rounded border border-gray-300 dark:border-gray-700 px-2 py-1 text-sm"
    />
  );
}

function ComposeButtonRow({
  preview,
  editable,
  onOpen,
}: {
  preview: string;
  editable: boolean;
  onOpen: () => void;
}) {
  return (
    <div className="space-y-2">
      <pre className="overflow-x-auto rounded border border-gray-200 dark:border-gray-800 bg-gray-50 dark:bg-gray-800 p-2 text-xs">
        {preview}
      </pre>
      {!editable && (
        <p className="text-xs text-gray-500">
          Code-owned: generated from the built-in template and guard-tested. Read-only -
          tune per-tenant behaviour via the typed tenant settings (image tag, storage quota,
          telemetry) that feed it as env vars.
        </p>
      )}
      <button
        type="button"
        onClick={onOpen}
        className="rounded bg-gray-800 px-3 py-1.5 text-xs font-medium text-white hover:bg-gray-900"
      >
        {editable ? 'Open compose editor →' : 'View compose template →'}
      </button>
    </div>
  );
}

// ---- ComposeEditor -------------------------------------------------

function ComposeEditor({
  currentValue,
  editable,
  onClose,
  onSaved,
}: {
  currentValue: string;
  editable: boolean;
  onClose: () => void;
  onSaved: () => Promise<void>;
}) {
  const [draft, setDraft] = useState(currentValue);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [preview, setPreview] = useState<string | null>(null);
  const [previewing, setPreviewing] = useState(false);
  const [showDiff, setShowDiff] = useState(false);

  const dirty = editable && draft !== currentValue;
  const diffLines = useMemo(() => (showDiff ? computeLineDiff(currentValue, draft) : []), [
    showDiff,
    currentValue,
    draft,
  ]);

  const handleTestParse = useCallback(async () => {
    setError(null);
    setPreview(null);
    setPreviewing(true);
    try {
      const r = await api<{ rendered: string }>('/v1/admin/settings/test-compose', {
        method: 'POST',
        body: { template: draft },
      });
      setPreview(r.rendered);
    } catch (err) {
      if (err instanceof ApiError) {
        const body = err.body as { message?: string } | null;
        setError(body?.message ?? `render failed (HTTP ${err.status})`);
      } else {
        setError('render failed');
      }
    } finally {
      setPreviewing(false);
    }
  }, [draft]);

  const handleSave = useCallback(async () => {
    setError(null);
    setSaving(true);
    try {
      // Quick client-side sanity check: must look like YAML (not empty,
      // has at least one `:`).
      if (!draft.trim() || !draft.includes(':')) {
        setError('Template looks empty or not YAML — refusing to save.');
        setSaving(false);
        return;
      }
      await api('/v1/admin/settings/tenant.compose_template', {
        method: 'PATCH',
        body: { value: draft },
      });
      await onSaved();
    } catch (err) {
      if (err instanceof ApiError && err.status === 400) {
        const body = err.body as { reason?: string } | null;
        setError(body?.reason ?? 'save failed');
      } else if (err instanceof Error) {
        setError(err.message);
      } else {
        setError('save failed');
      }
    } finally {
      setSaving(false);
    }
  }, [draft, onSaved]);

  return (
    <div className="fixed inset-0 z-50 flex flex-col bg-black/40 p-4 sm:p-8">
      <div className="flex h-full flex-col rounded-lg bg-white dark:bg-gray-900 shadow-xl">
        <header className="flex items-center justify-between border-b border-gray-200 dark:border-gray-800 px-4 py-3">
          <div>
            <h2 className="text-base font-semibold text-gray-900 dark:text-gray-100">
              Tenant compose template{!editable && ' (read-only)'}
            </h2>
            <p className="text-xs text-gray-600 dark:text-gray-400">
              {editable
                ? 'Changes apply to NEW tenants only. Existing tenants keep their original compose. Test in a dev redeem first.'
                : 'Code-owned: this is the exact compose the OCP generates for every new tenant. Read-only. Use "Render sample" to preview it with example env values.'}
            </p>
          </div>
          <button
            type="button"
            onClick={() => {
              if (dirty && !window.confirm('Discard unsaved changes?')) return;
              onClose();
            }}
            className="rounded border border-gray-300 dark:border-gray-700 px-3 py-1 text-sm hover:bg-gray-50 dark:hover:bg-gray-800/60"
          >
            Close
          </button>
        </header>

        <div className="flex flex-1 overflow-hidden">
          <div className="flex flex-1 flex-col">
            <div className="flex items-center justify-between border-b border-gray-200 dark:border-gray-800 px-4 py-2 text-xs text-gray-600 dark:text-gray-400">
              <span>
                {draft.split('\n').length} lines · {draft.length} chars
                {dirty && <span className="ml-2 font-medium text-amber-700 dark:text-amber-300">● unsaved</span>}
              </span>
              {editable && (
                <label className="flex items-center gap-1">
                  <input
                    type="checkbox"
                    checked={showDiff}
                    onChange={(e) => setShowDiff(e.target.checked)}
                  />
                  <span>Show diff against current</span>
                </label>
              )}
            </div>
            <textarea
              value={draft}
              onChange={(e) => editable && setDraft(e.target.value)}
              readOnly={!editable}
              spellCheck={false}
              className="flex-1 w-full resize-none border-0 px-4 py-3 font-mono text-xs text-gray-900 dark:text-gray-100 focus:outline-none"
              style={{ tabSize: 2 }}
            />
          </div>

          {(showDiff || preview) && (
            <div className="w-1/2 border-l border-gray-200 dark:border-gray-800 overflow-y-auto bg-gray-50 dark:bg-gray-800">
              {showDiff && (
                <div className="border-b border-gray-200 dark:border-gray-800 p-3">
                  <h3 className="text-xs font-semibold uppercase tracking-wide text-gray-500">
                    Diff (current vs draft)
                  </h3>
                  <pre className="mt-2 text-xs font-mono whitespace-pre-wrap">
                    {diffLines.length === 0
                      ? '(no changes)'
                      : diffLines.map((line, i) => (
                          <div
                            key={i}
                            className={
                              line.kind === 'add'
                                ? 'bg-green-100 text-green-900'
                                : line.kind === 'remove'
                                  ? 'bg-red-100 text-red-900 dark:text-red-200'
                                  : 'text-gray-700 dark:text-gray-300'
                            }
                          >
                            {line.prefix}
                            {line.text}
                          </div>
                        ))}
                  </pre>
                </div>
              )}

              {preview && (
                <div className="p-3">
                  <h3 className="text-xs font-semibold uppercase tracking-wide text-gray-500">
                    Rendered with sample env-vars
                  </h3>
                  <pre className="mt-2 overflow-x-auto rounded bg-white dark:bg-gray-900 p-2 text-xs font-mono">
                    {preview}
                  </pre>
                </div>
              )}
            </div>
          )}
        </div>

        {error && (
          <div role="alert" className="border-t border-red-200 dark:border-red-800 bg-red-50 dark:bg-red-900/30 px-4 py-2 text-sm text-red-800 dark:text-red-300">
            {error}
          </div>
        )}

        <footer className="flex items-center justify-between border-t border-gray-200 dark:border-gray-800 px-4 py-3">
          <div className="text-xs text-gray-600 dark:text-gray-400">
            {editable
              ? 'Existing tenants keep their original compose definition. Test in dev first.'
              : 'Read-only. To change what tenants run, edit renderTenantCompose() in orboto-saas-control-plane.'}
          </div>
          <div className="flex gap-2">
            <button
              type="button"
              onClick={handleTestParse}
              disabled={previewing}
              className="rounded border border-gray-300 dark:border-gray-700 px-3 py-1.5 text-sm hover:bg-gray-50 dark:hover:bg-gray-800/60 disabled:opacity-50"
            >
              {previewing ? 'Rendering…' : editable ? 'Test parse' : 'Render sample'}
            </button>
            {editable && (
              <button
                type="button"
                onClick={handleSave}
                disabled={!dirty || saving}
                className="rounded bg-brand-500 px-3 py-1.5 text-sm font-medium text-white hover:bg-brand-600 disabled:cursor-not-allowed disabled:opacity-50"
              >
                {saving ? 'Saving…' : 'Save compose template'}
              </button>
            )}
          </div>
        </footer>
      </div>
    </div>
  );
}

// ---- shared sub-components ----------------------------------------

function SourceBadge({ source }: { source: Source }) {
  const styles: Record<Source, string> = {
    db: 'bg-brand-100 text-brand-800',
    env: 'bg-amber-100 text-amber-800 dark:text-amber-200',
    default: 'bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-300',
  };
  const labels: Record<Source, string> = {
    db: 'custom',
    env: 'from env',
    default: 'default',
  };
  return (
    <span className={`shrink-0 rounded px-1.5 py-0.5 text-xs font-medium ${styles[source]}`}>
      {labels[source]}
    </span>
  );
}

// ---- value (de)serialisation for the inputs ----------------------

function formatForInput(value: unknown, type: ValueType): string {
  if (value == null) return '';
  if (type === 'boolean') return value === true ? 'true' : 'false';
  if (type === 'number') return String(value);
  if (type === 'string[]') return Array.isArray(value) ? value.join(', ') : String(value);
  if (type === 'object') return JSON.stringify(value, null, 2);
  return String(value);
}

function formatForLabel(value: unknown, type: ValueType): string {
  if (value == null) return '—';
  if (type === 'boolean') return value === true ? 'true' : 'false';
  if (type === 'string[]') return Array.isArray(value) ? value.join(', ') : String(value);
  if (type === 'object') return JSON.stringify(value);
  if (type === 'text-multiline') {
    const s = String(value);
    return s.length > 40 ? s.slice(0, 40) + '…' : s;
  }
  return String(value);
}

function parseFromInput(raw: string, type: ValueType): unknown {
  if (type === 'boolean') return raw === 'true';
  if (type === 'number') {
    const n = Number(raw);
    if (Number.isNaN(n)) throw new Error('value is not a number');
    return n;
  }
  if (type === 'string[]') {
    return raw
      .split(',')
      .map((s) => s.trim())
      .filter(Boolean);
  }
  if (type === 'object') {
    try {
      return JSON.parse(raw);
    } catch (err) {
      throw new Error(`invalid JSON: ${err instanceof Error ? err.message : String(err)}`);
    }
  }
  return raw;
}

// ---- line diff ----------------------------------------------------

interface DiffLine {
  kind: 'add' | 'remove' | 'context';
  prefix: string;
  text: string;
}

/**
 * Tiny line-based diff. Not LCS-optimal, but adequate for spotting
 * what changed in a compose template: shows added + removed lines
 * inline. For a heavier diff library, we'd swap this for `diff`.
 */
function computeLineDiff(a: string, b: string): DiffLine[] {
  const oldLines = a.split('\n');
  const newLines = b.split('\n');
  const oldSet = new Set(oldLines);
  const newSet = new Set(newLines);
  const out: DiffLine[] = [];
  let i = 0;
  let j = 0;
  while (i < oldLines.length || j < newLines.length) {
    const oldLine = oldLines[i];
    const newLine = newLines[j];
    if (oldLine === newLine) {
      // Context only when surrounding a change — to keep the panel short,
      // emit a compact "(N unchanged)" instead of every line.
      // For now keep simple: emit context lines only when they're <80 chars.
      if (oldLine !== undefined && oldLine.length < 120) {
        out.push({ kind: 'context', prefix: '  ', text: oldLine });
      }
      i++;
      j++;
      continue;
    }
    if (oldLine !== undefined && !newSet.has(oldLine)) {
      out.push({ kind: 'remove', prefix: '- ', text: oldLine });
      i++;
      continue;
    }
    if (newLine !== undefined && !oldSet.has(newLine)) {
      out.push({ kind: 'add', prefix: '+ ', text: newLine });
      j++;
      continue;
    }
    // Both lines exist on opposite sides but in different positions.
    // Emit as remove/add pair.
    if (oldLine !== undefined) {
      out.push({ kind: 'remove', prefix: '- ', text: oldLine });
      i++;
    }
    if (newLine !== undefined) {
      out.push({ kind: 'add', prefix: '+ ', text: newLine });
      j++;
    }
  }
  return out;
}

interface ProviderIdentityPreview {
  identity: { entity_type: 'natural_person' | 'company' };
  missing: string[];
  sends_blocked: boolean;
  text: { de: string; en: string };
}

/**
 * OCP-710 - the provider identification at the foot of every mail, as the next
 * send will carry it. Reloads when the settings list reloads, so a saved
 * `mail.company.*` value shows at once. Missing required fields block every send.
 */
function MailProviderIdentityPreview({ version }: { version: unknown }) {
  const [data, setData] = useState<ProviderIdentityPreview | null>(null);
  useEffect(() => {
    api<ProviderIdentityPreview>('/v1/admin/mail/provider-identity')
      .then(setData)
      .catch(() => setData(null));
  }, [version]);
  if (!data) return null;
  return (
    <section data-testid="mail-provider-identity" className="mb-4 rounded border border-gray-200 p-3 text-sm dark:border-gray-700">
      <h3 className="mb-1 font-semibold text-gray-900 dark:text-gray-100">Provider identification on every mail</h3>
      <p className="mb-2 text-xs text-gray-600 dark:text-gray-400">
        Rendered from the <code>mail.company.*</code> settings below ({data.identity.entity_type === 'company' ? 'company: name with legal form, seat, register court and number, managing directors' : 'natural person: name, address, contact, imprint'}). It must say what the imprint says.
      </p>
      {data.sends_blocked && (
        <p role="alert" className="mb-2 rounded bg-red-50 px-2 py-1 text-xs text-red-800 dark:bg-red-900/30 dark:text-red-300">
          Every mail send is refused until these are set: {data.missing.join(', ')}
        </p>
      )}
      <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
        {(['de', 'en'] as const).map((l) => (
          <pre key={l} data-testid={`mail-provider-identity-${l}`} className="whitespace-pre-wrap rounded bg-gray-50 p-2 text-xs text-gray-700 dark:bg-gray-900 dark:text-gray-300">{data.text[l]}</pre>
        ))}
      </div>
    </section>
  );
}

